/**
 * SidebarItem - single navigation link with hover, active, focus states.
 * Displays icon + label when expanded, icon-only with tooltip when collapsed.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type { NavItem } from './navConfig';

interface Props {
  item: NavItem;
  collapsed: boolean;
  locked?: boolean;
}

const ItemLink = styled(Link, {
  shouldForwardProp: p => p !== 'active' && p !== 'collapsed' && p !== 'locked',
})<{ active: boolean; collapsed: boolean; locked: boolean }>(
  ({ theme, active, collapsed, locked }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: collapsed ? '8px 0' : '8px 12px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 6,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    position: 'relative',
    transition: 'background 120ms ease, color 120ms ease, opacity 120ms ease',
    color: active ? theme.tokens.sidebarItemActiveFg : theme.tokens.fgSecondary,
    backgroundColor: active ? theme.tokens.sidebarItemActive : 'transparent',
    opacity: locked ? 0.55 : 1,

    '&::before': active
      ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 4,
          bottom: 4,
          width: 3,
          borderRadius: 2,
          backgroundColor: theme.tokens.sidebarItemActiveBar,
        }
      : {},

    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: active ? theme.tokens.sidebarItemActiveFg : theme.tokens.fgTertiary,
      flexShrink: 0,
    },

    '&:hover': {
      backgroundColor: active ? theme.tokens.sidebarItemActive : theme.tokens.sidebarItemHover,
      color: active ? theme.tokens.sidebarItemActiveFg : theme.tokens.fgPrimary,
      opacity: locked ? 0.75 : 1,
      '& .MuiSvgIcon-root': {
        color: active ? theme.tokens.sidebarItemActiveFg : theme.tokens.fgPrimary,
      },
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.tokens.accent}`,
      outlineOffset: 2,
    },
  })
);

const LockBadge = styled(LockOutlinedIcon)(({ theme }) => ({
  fontSize: '14px !important',
  color: `${theme.tokens.fgTertiary} !important`,
  marginLeft: 'auto',
  flexShrink: 0,
}));

const Label = styled('span')<{ collapsed: boolean }>(({ collapsed }) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  opacity: collapsed ? 0 : 1,
  width: collapsed ? 0 : 'auto',
  transition: 'opacity 100ms ease, width 100ms ease',
}));

export default function SidebarItem({ item, collapsed, locked = false }: Props) {
  const location = useLocation();
  const active =
    item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);

  const content = (
    <ItemLink to={item.to} active={active} collapsed={collapsed} locked={locked}>
      {item.icon}
      <Label collapsed={collapsed}>{item.label}</Label>
      {locked && !collapsed && <LockBadge />}
    </ItemLink>
  );

  if (collapsed || locked) {
    return (
      <Tooltip
        title={
          <>
            <div style={{ fontWeight: 600 }}>{item.label}</div>
            {locked ? (
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                No access. Contact your administrator.
              </div>
            ) : (
              item.description && (
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{item.description}</div>
              )
            )}
          </>
        }
        placement="right"
        enterDelay={200}
        arrow
      >
        {content}
      </Tooltip>
    );
  }

  return content;
}
